import type { ChatDocumentDTO } from '@shared/schemas/chat';
import {
  documentGenerationToolInputSchema,
  documentGenerationToolOutputSchema,
} from '@shared/schemas/document-generation';
import { CODEGEN_DOCUMENT_ID_PREFIX } from './codegen-document';
import type { ChatUIMessage, ChatUIFilePart } from './types';

export type ConversationDocumentEntry = ChatDocumentDTO & {
  codegenContext?: {
    conversationId: string;
    generationId: string;
  };
  createdAt?: string;
};

type DocumentPart = Extract<ChatUIMessage['parts'][number], {
  type: 'data-document-finish' | 'data-document-open' | 'data-document-preview';
}>;

type ToolDisplayPart = ChatUIMessage['parts'][number] & {
  state?: string;
  input?: unknown;
  output?: unknown;
};

function getDocumentPartPriority(part: DocumentPart) {
  if (part.data.status === 'ready' || part.data.status === 'error') {
    return 3;
  }

  if (part.type === 'data-document-finish') {
    return 2;
  }

  return 1;
}

function getDocumentParts(message: ChatUIMessage) {
  const partsByDocumentId = new Map<string, DocumentPart>();

  message.parts
    .filter((part): part is DocumentPart => (
      part.type === 'data-document-finish'
      || part.type === 'data-document-open'
      || part.type === 'data-document-preview'
    ))
    .forEach((part) => {
      const current = partsByDocumentId.get(part.data.documentId);

      if (!current || getDocumentPartPriority(part) >= getDocumentPartPriority(current)) {
        partsByDocumentId.set(part.data.documentId, part);
      }
    });

  return Array.from(partsByDocumentId.values()).map((part) => part.data);
}

function getFileParts(message: ChatUIMessage): ChatUIFilePart[] {
  // Persisted file parts follow the shared DTO (optional url/attachmentId),
  // which is wider than the AI SDK FileUIPart — same seam as toChatUiMessage.
  return message.parts
    .filter((part) => part.type === 'file')
    .map((part) => part as unknown as ChatUIFilePart);
}

function isDocumentGenerationToolPart(part: ChatUIMessage['parts'][number]): part is ToolDisplayPart {
  if (!('type' in part) || !('state' in part)) {
    return false;
  }

  const rawName = part.type === 'dynamic-tool'
    ? (part as ToolDisplayPart & { toolName?: string }).toolName ?? ''
    : part.type.replace(/^tool-/, '');

  return rawName === 'generate_document' || rawName === 'revise_document';
}

function buildAttachmentDocument(part: ChatUIFilePart, messageId: string, apiBaseUrl: string): ConversationDocumentEntry {
  const attachmentId = part.attachmentId ?? part.url ?? `${messageId}-file`;

  return {
    content: '',
    documentId: attachmentId,
    filename: part.filename ?? null,
    kind: 'attachment',
    mediaType: part.mediaType,
    sourceUrl: part.url ?? (part.attachmentId
      ? new URL(`/api/chat/attachments/${part.attachmentId}`, apiBaseUrl).toString()
      : null),
    status: 'ready',
    summary: part.filename ?? part.mediaType,
    title: part.filename ?? 'Fichier joint',
  };
}

const DOCUMENT_GENERATION_KIND_MIME: Record<string, string> = {
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  pdf: 'application/pdf',
  pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
};

function getCodegenDocuments(message: ChatUIMessage): ConversationDocumentEntry[] {
  return message.parts
    .filter(isDocumentGenerationToolPart)
    .flatMap((toolPart) => {
      if (toolPart.state !== 'output-available') {
        return [];
      }

      const parsedOutput = documentGenerationToolOutputSchema.safeParse(toolPart.output);
      if (!parsedOutput.success || parsedOutput.data.status !== 'pending') {
        return [];
      }

      const parsedInput = documentGenerationToolInputSchema.safeParse(toolPart.input);
      const title = parsedOutput.data.title
        ?? (parsedInput.success ? parsedInput.data.title : 'Document généré');
      const generationKind = parsedOutput.data.kind
        ?? (parsedInput.success ? parsedInput.data.kind : null);

      return [{
        codegenContext: {
          conversationId: parsedOutput.data.conversationId,
          generationId: parsedOutput.data.generationId,
        },
        content: '',
        documentId: `${CODEGEN_DOCUMENT_ID_PREFIX}${parsedOutput.data.generationId}`,
        filename: null,
        kind: 'attachment',
        // Donner le bon type MIME pour que l'icône affiche le visuel Word/PDF/...
        // (comme la carte de la conversation) plutôt que l'icône générique.
        mediaType: generationKind ? DOCUMENT_GENERATION_KIND_MIME[generationKind] ?? null : null,
        sourceUrl: null,
        status: 'ready',
        summary: null,
        title,
      }];
    });
}

function upsertDocument(
  documents: Map<string, ConversationDocumentEntry>,
  document: ConversationDocumentEntry,
) {
  const existing = documents.get(document.documentId);

  if (!existing) {
    documents.set(document.documentId, document);
    return;
  }

  if (existing.status === 'streaming' && document.status !== 'streaming') {
    documents.set(document.documentId, { ...existing, ...document });
    return;
  }

  if (document.sourceUrl && !existing.sourceUrl) {
    documents.set(document.documentId, { ...existing, ...document });
  }
}

export function collectConversationDocuments(
  messages: ChatUIMessage[],
  apiBaseUrl: string,
): ConversationDocumentEntry[] {
  const documents = new Map<string, ConversationDocumentEntry>();

  for (const message of messages) {
    const createdAt = message.metadata?.createdAt;

    for (const document of getDocumentParts(message)) {
      upsertDocument(documents, { ...document, createdAt });
    }

    for (const filePart of getFileParts(message)) {
      upsertDocument(documents, { ...buildAttachmentDocument(filePart, message.id, apiBaseUrl), createdAt });
    }

    for (const document of getCodegenDocuments(message)) {
      upsertDocument(documents, { ...document, createdAt });
    }
  }

  return Array.from(documents.values());
}
